import app from 'app';
import _ from 'lodash-extended';
import WallpapersStoreModule from './wallpapers.vuex';
import TimemachineStoreModule from '../../views/Story/Timeline/data/timemachine.vuex';
import timemachineTaskManager from 'v/Story/Timeline/api/timemachineTaskManager';
import videoStyle from './videoStyle.json';
import { isAssetCandidateForTranscribe } from 'v/Story/Timeline/api/transcription';
import StoryMutationPlugin from '../plugins/StoryMutationPlugin';
import {
  addColorIfNotExist,
  normalizeColor,
  normalizeColorSet,
  logColorSet,
} from '#/utils/colour';
import { workPool } from '@wochitnpm/helpers';

export default {
  plugins: [StoryMutationPlugin],

  state() {
    return {
      id: null,
      programmerId: null,

      _assetsSource: app.assetsSource.NORMAL,
      entityNames: [],
      seoTags: [],
      annotations: [],
      storyUrl: null,
      enableVOSubtitles: null,
      enableVOWaveform: null,
      links: null,
      linksText: null,
      linksAndLinksText: null,
      renderPublishDate: null,
      lockMetadataEdit: null,
      title: null,
      description: null,
      attributedBy: null,
      comment: null,
      narratedText: null,
      content: null,
      taskType: null,

      lightboxUploadingAssets: [],
      lightboxUploadingVoiceOverAssets: [],
      selectedAssets: [],
      selectedAssetsFilter: null,
      selectedAssetsSearchQuery: null,
      selectedVoiceOverSegments: [],
      audioTracks: [],
      storyBackgroundMusic: null,
      reDraggableAssets: null,

      // utilitarian - sets of unsubscribers from listeners
      workPoolHandlers: new Set(),
      pluginOffHandlers: new Set(),

      // when last time asset was selected
      addSelectedAsset_timestamp: null,

      themeInfo: null,

      finalStoryDuration: null,
      timelineDuration: null,

      timelineUndoRedo: {
        isEnabled: true,
        canUndo: null,
        canRedo: null,
      },

      timelineZoom: {
        steps: [240, 120, 60, 40, 30, 20, 10, 5],
        // steps: _.map(new Array(239), (v, i) => i+2).reverse(),
        rowDuration: app.DEFAULT.ROW_DURATION,
      },

      transcriptions: {},
      transcriptLastModified: null,
      audioWaveForms: {},
      isTemplate: null,

      fonts: [],
      selectedFonts: {
        font1: null,
        font2: null,
      },
      originalFonts: {
        font1: null,
        font2: null,
      },
      defaultFonts: {
        font1: null,
        font2: null,
      },
      colorSets: [],
      recentColorSets: [],
      recentColorSetsCount: 3,
      selectedColorSet: {
        color1: null,
        color2: null,
        color3: null,
        color4: null,
      },
      originalColorSet: {
        color1: null,
        color2: null,
        color3: null,
        color4: null,
      },
      bgMusicBeats: [],
      bgMusicBeatsIsLoading: false,

      selectedThemeID: null,
      brandId: null,

      scenes: {}, // Map of assetUUID -> scenes array
    };
  },

  // @params (state, getters, rootState, rootGetters)
  getters: {
    assetsSource: (state) => state._assetsSource,
    entityNames: (state) => state.entityNames,
    seoTags: (state) => state.seoTags,
    annotations: (state) => state.annotations,
    description: (state) => state.description,
    storyUrl: (state) => state.storyUrl,
    content: (state) => state.content,
    fromFeed: (state) => state.storyStartPoint === app.storyStartPoint.FEED,

    storyProgrammer(state) {
      const programmerModel = app.programmers.get(state.programmerId);
      return programmerModel ? programmerModel.attributes : null;
    },

    serializedAssets(state, getters) {
      return app.namespace.Controller.AssetController.serialize(
        getters.selectedAssets
      );
    },

    serializedVoiceOverSegments(state, getters) {
      const segments = _.filter(
        getters.selectedVoiceOverSegments,
        (segment) => !segment._deleted
      );
      if (segments?.length) {
        return segments.map((segment) => {
          const res = _.cloneDeep(segment);
          res.audioSegmentContent.asset =
            app.namespace.Controller.AssetController.serializeAsset(
              res.audioSegmentContent.asset
            );
          return res;
        });
      }
      return segments;
    },

    selectedAssets(state) {
      // descending order
      return _.sortBy(
        state.selectedAssets,
        (asset) => -(asset.dateSelected || $.now())
      );
    },

    storyBrand(state, getters) {
      return (
        getters['user/selectedOrganization'].brands.find(
          (brand) =>
            brand.brandId === state.brandId &&
            brand.programmerId === state.programmerId
        ) || null
      );
    },

    selectedVoiceOverSegments(state) {
      // descending order
      return _.sortBy(
        state.selectedVoiceOverSegments,
        (segment) =>
          -(segment.audioSegmentContent.asset.metadata.creationDate || $.now())
      );
    },

    hasAudioOnTimeline(state) {
      return !_.isEmpty(state.audioTracks);
    },

    showStyleEditor(state, getters, rootState, rootGetters) {
      return (
        state.themeInfo.showStyleEditor &&
        rootGetters['user/isTemplateSpecialist']
      );
    },

    themeOverrides(state) {
      const themeVariantOverrides = [];
      const colors = _.clone(state.selectedColorSet);
      colors['color3'] = colors['color3'] || colors['color1'];
      colors['color4'] = colors['color4'] || colors['color2'];
      for (const color of ['color1', 'color2', 'color3', 'color4']) {
        let value = colors[color];
        if (value) {
          if (value[0] === '#') {
            value = value.substring(1);
          }
          themeVariantOverrides.push({
            type: 'color',
            name: color,
            value,
          });
        }
      }
      for (const font of ['font1', 'font2']) {
        let value = state.selectedFonts[font];
        if (typeof value === 'string') {
          value = state.selectedFonts[font].replace('__', '/');
        }
        if (value) {
          themeVariantOverrides.push({
            type: 'font',
            name: font,
            value,
          });
        }
      }
      return {
        themeVariantOverrides,
      };
    },

    /**
     * acquire unique assets (by assetUUID) from lightbox and timeline
     * that are potential candidates for transcribe
     */
    visualCandidatesForTranscribe(state, getters) {
      let rv = [];

      const tmCandidates = _.filter(
        getters['timemachine/visualData'],
        (asset) => {
          return isAssetCandidateForTranscribe(asset);
        }
      );
      const selectedCandidates = _.filter(state.selectedAssets, (asset) => {
        return isAssetCandidateForTranscribe(asset);
      });
      rv = _.uniqBy(
        tmCandidates.concat(selectedCandidates),
        'metadata.assetUUID'
      );

      return rv;
    },

    isSomeTranscribing(state) {
      return _.some(state.transcriptions, (t) => {
        return /PROCESSING/.test(t.status);
      });
    },

    /**
     * Categorizes lightbox assets into different types
     * Returns an object with arrays of UUIDs for:
     * - imageVideoUUIDs: Image/video assets ready to use
     * - uploadingUUIDs: Assets currently being uploaded
     * - otherAssetsUUIDs: Other types of assets
     * - audioUUIDs: Audio/voice over assets
     */
    lightboxAssetUUIDs(state) {
      const selectedAssets = state.selectedAssets || [];
      const uploadingAssets = state.lightboxUploadingAssets || [];

      // Helper functions
      const isImageVideo = (asset) =>
        app.assetType.isIMAGE(asset) ||
        app.assetType.isVIDEO(asset) ||
        app.assetType.isFRAGMENT(asset) ||
        app.assetType.isSOCIALIMAGE(asset);

      const isUploading = (asset) => {
        const status = asset.metadata?.uploadStatus;
        return (
          status === app.uploadStatus.PROCESSING ||
          status === app.uploadStatus.UPLOAD
        );
      };

      const result = {
        imageVideoUUIDs: [],
        hasUploadingAssets: uploadingAssets.length > 0,
        hasOtherAssets: false,
        audioUUIDs: [],
      };

      const processedUUIDs = new Set();

      // Process selected assets
      selectedAssets.forEach((asset) => {
        const uuid = asset.metadata?.assetUUID;
        if (!uuid) return;

        processedUUIDs.add(uuid);

        if (isImageVideo(asset)) {
          if (isUploading(asset)) {
            result.hasUploadingAssets = true;
          } else {
            result.imageVideoUUIDs.push(uuid);
          }
        } else {
          result.hasOtherAssets = true;
        }
      });

      // Process uploading assets (only new ones)
      // uploadingAssets.forEach((asset) => {
      //   const uuid = asset.metadata?.assetUUID;
      //   if (!uuid || processedUUIDs.has(uuid)) return;
      //   result.uploadingUUIDs.push(uuid);
      // });

      // Process all audio assets
      state.selectedVoiceOverSegments.forEach((audioAsset) => {
        let uuid;
        if (audioAsset._deleted) {
          return;
        }
        if (audioAsset.metadata?.assetUUID) {
          // It's an uploading voice over asset
          uuid = audioAsset.metadata.assetUUID;
          result.audioUUIDs.push(uuid);
        } else if (audioAsset.audioSegmentContent?.asset?.metadata?.assetUUID) {
          // It's a voice over segment
          uuid = audioAsset.audioSegmentContent.asset.metadata.assetUUID;
          result.audioUUIDs.push(uuid);
        }
      });

      if (state.lightboxUploadingVoiceOverAssets?.length > 0) {
        result.hasUploadingAssets = true;
      }

      return result;
    },

    /**
     * Returns true if there are any assets currently being uploaded
     */
    hasUploadingAssets(state, getters) {
      return !!getters.lightboxAssetUUIDs.hasUploadingAssets;
    },

    /**
     * Returns true if there are any image or video assets in the lightbox
     */
    hasImageVideoAssets(state, getters) {
      return getters.lightboxAssetUUIDs.imageVideoUUIDs.length > 0;
    },

    /**
     * Returns true if there are any voice over assets in the lightbox
     */
    hasVoiceOverAssets(state, getters) {
      return getters.lightboxAssetUUIDs.audioUUIDs.length > 0;
    },
  },

  // @params (state, payload)
  mutations: {
    storyInit(state, { story, remoteStash }) {
      state.id = story.id;
      state.programmerId = story.programmerId;
      state.title = story.title;
      state.description = story.description;
      state.attributedBy = story.attributedBy;
      state.entityNames = story.entityNames;
      state.comment = story.comment;
      state.narratedText = story.narratedText;
      state.content = story.content;
      state.storyId = story.id;
      state.storyUrl = story.storyUrl;
      state.enableVOSubtitles = !!story.enableVOSubtitles;
      state.enableVOWaveform = !!story.enableVOWaveform;
      state.links = story.linksList;
      state.linksText = story.linksTextList;
      state.linksAndLinksText = story.linksAndLinksTextList;
      state.renderPublishDate = story.renderPublishDate;
      state.lockMetadataEdit = story.lockMetadataEdit;
      state.remoteStash = remoteStash;
      state.storyStartPoint = story.storyStartPoint;
      state.themeInfo = _.find(story._themeInfo, {
        aspectRatio: story.aspectRatio,
      });
      state.storyBackgroundMusic = null;
      state.selectedVoiceOverSegments = story.segments || [];
      state.audioTracks = story.audioTracks || [];
      state.isTemplate = story.template;
      state.fonts = story._rawFonts;
      state.brandId = story.brandId;
    },
    setSelectedAssetsFilter(state, filterFormat) {
      state.selectedAssetsFilter = filterFormat;
    },
    setSelectedAssetsSearchQuery(state, searchQuery) {
      state.selectedAssetsSearchQuery = searchQuery;
    },
    setBgMusicBeats(state, beats) {
      state.bgMusicBeatsIsLoading = false;
      state.bgMusicBeats = beats;
    },
    setBgMusicBeatsStatus(state) {
      state.bgMusicBeatsIsLoading = true;
    },
    clearBgMusicBeats(state) {
      state.bgMusicBeats = [];
    },

    setColorSets(state, colorSets) {
      state.colorSets = colorSets;
    },

    setSelectedColorSet(state, colors) {
      state.selectedColorSet = colors.reduce((acc, el) => {
        acc[el.name] = el.value;
        return acc;
      }, {});
    },

    setSelectedFont(state, font) {
      state.selectedFonts[font.name] = font.value;
    },

    setSelectedFonts(state, fonts) {
      state.selectedFonts = fonts.reduce((acc, el) => {
        acc[el.name] = el.value;
        return acc;
      }, {});
    },

    setOriginalFonts(state, fonts) {
      state.originalFonts = fonts.reduce((acc, el) => {
        acc[el.name] = el.value;
        return acc;
      }, {});
    },

    setDefaultFonts(state, fonts) {
      state.defaultFonts = fonts.reduce((acc, el) => {
        acc[el.name] = el.value;
        return acc;
      }, {});
    },

    resetVideoStyle(state) {
      state.selectedColorSet = _.cloneDeep(state.originalColorSet);
      state.selectedFonts = _.cloneDeep(state.originalFonts);
    },

    setOriginalColorSet(state, colors) {
      state.originalColorSet = colors.reduce((acc, el) => {
        acc[el.name] = el.value;
        return acc;
      }, {});
    },

    addRecentColorSet(state) {
      const selectedColorSet = _.clone(state.selectedColorSet);
      const key = 'recent_color_sets';
      let recentColorSets = app.remoteStash.get(key);
      if (recentColorSets) {
        recentColorSets = JSON.parse(recentColorSets);
      } else {
        recentColorSets = [];
      }

      const lookup = _.findIndex(state.recentColorSets, selectedColorSet);
      if (lookup >= 0) {
        // selected color set exists in recent color sets
        recentColorSets.splice(lookup, 1); // delete it
      }
      recentColorSets.splice(0, 0, selectedColorSet); // put it in the beginning of the array
      recentColorSets.splice(state.recentColorSetsCount);
      app.remoteStash.set(key, JSON.stringify(recentColorSets));
      state.recentColorSets = recentColorSets;
    },

    setRecentColorSets(state) {
      const recentColorSets = app.remoteStash.get('recent_color_sets');
      if (recentColorSets) {
        state.recentColorSets = JSON.parse(recentColorSets);
      }
    },

    setStoryTemplate(state, value) {
      state.isTemplate = value;
    },

    setStoryBackgroundMusic(state, value) {
      state.storyBackgroundMusic = value;
    },

    addSelectedAsset(state, { asset, isUserSelection = false }) {
      state.selectedAssets.push(asset);
    },

    addSelectedVoiceOverSegment(state, { asset }) {
      const audioSegmentContent = {
        asset,
        cdnUrl: asset._narrationData?.cdnUrl || asset.metadata.url,
        url: asset._narrationData?.url || asset.metadata.url,
      };
      delete asset._narrationData;
      const segment = {
        guid: app.guid(),
        text: '',
        audioSegmentContent,
      };
      state.selectedVoiceOverSegments.push(segment);
    },

    undeleteVoiceOverSegment(state) {
      _.each(state.selectedVoiceOverSegments, (segment) => {
        const inUse = _.some(state.audioTracks, {
          assetUUID: segment.audioSegmentContent.asset.metadata.assetUUID,
        });
        if (inUse) {
          app.Vue.set(segment, '_deleted', false);
        }
      });
    },

    setAudioTracks(state, value) {
      state.audioTracks = value;
    },

    removeSelectedAsset(state, asset) {
      let index = state.selectedAssets.findIndex((el) => el.id === asset.id);
      if (index === -1 && asset.metadata.providerAssetId) {
        // [DEV-8248] - trying to remove asset from gallery that have different id in lightbox
        index = state.selectedAssets.findIndex(
          (el) =>
            el.metadata.providerAssetId === asset.metadata.providerAssetId &&
            el.metadata.providerId === asset.metadata.providerId
        );
      }

      if (index >= 0) {
        state.selectedAssets.splice(index, 1);
      }
    },

    removeSelectedVoiceOverSegment(state, segment) {
      app.Vue.set(segment, '_deleted', true);
    },

    selectedAssetMetadataEdited(state, payload) {
      _.each(state.selectedAssets, (asset) => {
        if (asset.metadata.assetUUID === payload.assetUUID) {
          asset.metadata = Object.assign(asset.metadata, payload.metadata);
        }
      });
    },

    updateSelectedAsset(
      state,
      { id, wallpaperId, wallpaperType, wallpaperData, wallpaperName }
    ) {
      const asset = _.find(state.selectedAssets, { id: id });
      asset.metadata.caption =
        wallpaperType === 'SOLID'
          ? app.getHexString(wallpaperData.colorValue)
          : 'GRADIENT';
      asset.wallpaperId = wallpaperId;
      asset.wallpaperMetaData.type = wallpaperType;
      asset.wallpaperMetaData.wallpaperData = wallpaperData;
      asset.colorName = wallpaperName;
    },

    triggerReDraggableAssets(state) {
      state.reDraggableAssets = Date.now();
    },

    setAssetsSource(state, source) {
      state._assetsSource = source;
    },

    addLightboxUploadingVoiceOverAsset(state, { asset }) {
      state.lightboxUploadingVoiceOverAssets.unshift(asset);
    },

    addLightboxUploadingAsset(state, { asset }) {
      state.lightboxUploadingAssets.unshift(asset);
    },

    removeLightboxUploadingAsset(state, asset) {
      // first - try to find by instance
      let index = state.lightboxUploadingAssets.indexOf(asset);
      if (index < 0) {
        // fallback - try to find by assetUUID
        index = state.lightboxUploadingAssets.findIndex(
          (el) => el.id === asset.metadata.assetUUID
        );
      }

      if (index >= 0) {
        state.lightboxUploadingAssets.splice(index, 1);
      }
    },

    removeLightboxUploadingVoiceOverAsset(state, asset) {
      const arr = state.lightboxUploadingVoiceOverAssets;
      // first - try to find by instance
      let index = arr.indexOf(asset);
      if (index < 0) {
        // fallback - try to find by assetUUID
        index = arr.findIndex((el) => el.id === asset.metadata.assetUUID);
      }

      if (index >= 0) {
        arr.splice(index, 1);
      }
    },

    setPinnedAsset(state, { assetId, isPinned }) {
      const assets = _.flatten([
        state.selectedAssets,
        app.rootStore.state.assetGallery
          ? app.rootStore.state.assetGallery.assetSearchResults
          : [],
      ]).filter((a) => {
        return a && a.id === assetId;
      });

      assets.forEach((asset) => {
        asset.pinned = isPinned;
      });
    },
    changeThemeInfo(state, { themeInfo }) {
      state.themeInfo = themeInfo;
    },

    setFinalStoryDuration(state, value) {
      state.finalStoryDuration = value;
    },

    setTimelineDuration(state, value) {
      state.timelineDuration = value;
    },

    setTimelineZoom(state, obj) {
      _.each(obj, (value, prop) => {
        state.timelineZoom[prop] = value;
      });
    },

    setTimelineUndoRedo(state, obj) {
      _.each(obj, (value, prop) => {
        state.timelineUndoRedo[prop] = value;
      });
    },

    setEnableVOSubtitles(state, value) {
      state.enableVOSubtitles = value;
      app.storyController.story.hasBeenChanged();
    },

    setEnableVOWaveform(state, value) {
      state.enableVOWaveform = value;
      app.storyController.story.hasBeenChanged();
    },

    setTranscription(state, { assetUUID, value }) {
      let t = value;
      if (state.transcriptions[assetUUID]) {
        t = Object.assign(state.transcriptions[assetUUID], value);
      }
      app.Vue.set(state.transcriptions, assetUUID, t);
    },

    setAudioWaveForms(state, { assetUUID, success }) {
      app.Vue.set(state.audioWaveForms, assetUUID, success);
    },

    setScenes(state, { assetId, scenes }) {
      state.scenes[assetId] = scenes;
    },
  },

  // @param context {{state, rootState, commit, dispatch, getters, rootGetters}}
  // @param payload
  actions: {
    FILTER_SELECTED_ASSETS({ state, commit }, { filterFormat }) {
      commit('setSelectedAssetsFilter', filterFormat);
    },
    SEARCH_SELECTED_ASSETS({ state, commit }, { searchQuery }) {
      commit('setSelectedAssetsSearchQuery', searchQuery);
    },
    SET_BG_MUSIC_BEATS({ commit }, { beats }) {
      commit('setBgMusicBeats', beats);
    },
    SET_BG_MUSIC_BEATS_STATUS_PROGRESS({ commit }) {
      commit('setBgMusicBeatsStatus');
    },
    FETCH_BG_MUSIC_BEATS({ commit, state }, payload) {
      commit('clearBgMusicBeats');
      workPool.job.snapBeats.execute({
        asset: state.storyBackgroundMusic,
        checkIfExists: true,
      });
    },

    SET_SELECTED_COLOR_SET(
      { commit, dispatch, state, getters },
      { refreshPreview, colorSet }
    ) {
      commit('setSelectedColorSet', colorSet);
      if (refreshPreview) {
        app.timelineGateway.refreshRtpLookup({ resetUndoRedo: true });
        app.storyController.story.hasBeenChanged();
      }
    },

    SET_SELECTED_FONT({ commit, dispatch }, font) {
      commit('setSelectedFont', font);
      app.timelineGateway.refreshRtpLookup({ resetUndoRedo: true });
      app.storyController.story.hasBeenChanged();
    },

    SET_SELECTED_FONTS(
      { commit, dispatch, state },
      { selectedFonts, brandFonts, refreshPreview }
    ) {
      selectedFonts = selectedFonts || [];
      brandFonts = brandFonts || [];
      const setValueIfMissing = (fontName, fontList) => {
        const font = _.find(fontList, {
          name: fontName,
        });
        if (font) {
          const look = _.find(state.fonts, { fontFamily: font.value });
          if (!look) {
            font.value = state.fonts[0].fontFamily;
          }
        } else {
          fontList.push({
            name: fontName,
            value: state.fonts[0].fontFamily,
          });
        }
      };
      for (const fontName of ['font1', 'font2']) {
        setValueIfMissing(fontName, selectedFonts);
        setValueIfMissing(fontName, brandFonts);
      }
      let defaultFonts;
      if (brandFonts?.length) {
        defaultFonts = brandFonts;
      } else {
        defaultFonts = selectedFonts;
      }
      commit('setDefaultFonts', defaultFonts);
      commit('setSelectedFonts', selectedFonts);
      commit('setOriginalFonts', selectedFonts);
    },

    SET_ORIGINAL_COLOR_SET({ commit, dispatch, state }, payload) {
      commit('setOriginalColorSet', payload);
    },

    SET_COLOR_SETS({ commit }, colorSets) {
      commit('setColorSets', colorSets);
    },

    /**
     * fetch transcription on all potential videos of timeline
     */
    BULK_FETCH_TRANSCRIPTION({ state, getters, dispatch }) {
      const potentialAssets =
        getters['timemachine/visualsWithPotentialTranscript'];

      _.each(potentialAssets, (asset) => {
        const assetUUID = asset.metadata.assetUUID;
        const transcript = state.transcriptions[assetUUID];
        const shouldTranscribe = transcript.status === 'FAILED';

        if (shouldTranscribe) {
          dispatch('FETCH_TRANSCRIPTION', {
            asset,
            transcribeSubType: app.transcribeSubType.VIDEO,
            how: workPool.job.transcriptions.HOW.CREATE,
          });
        }
      });
    },

    FETCH_TRANSCRIPTION(
      { state, getters, dispatch },
      { asset, transcribeSubType, how, alertOnError = false, languageCode }
    ) {
      if (!workPool.job.transcriptions.isRunning(asset)) {
        dispatch('SET_TRANSCRIPTION', {
          assetUUID: asset.metadata.assetUUID,
          value: {
            transcribeSubType,
            status: 'PENDING',
            tokens: [],
            originalTokens: [],
            isOriginal: null,
          },
        });

        alertOnError =
          alertOnError || transcribeSubType === app.transcribeSubType.NARRATION;

        workPool.job.transcriptions.execute({
          asset,
          storyId: state.storyId,
          transcribeSubType,
          how,
          programmerAllowedTranscription:
            getters.storyProgrammer.allowedTranscription,
          alertOnError,
          languageCode,
        });
      }
    },

    SAVE_TRANSCRIPTION({ state, dispatch }, { tokens, assetUUID, isOriginal }) {
      const originalValues = _.cloneDeep(state.transcriptions[assetUUID]);
      const shouldPostNew = state.transcriptions[assetUUID].shouldPostNew;
      const didFail = originalValues.status === 'FAILED';

      if (didFail || shouldPostNew) {
        dispatch('SET_TRANSCRIPTION', {
          assetUUID,
          value: {
            status: 'DONE',
            isOriginal: true,
            tokens,
            originalTokens: tokens,
            shouldPostNew: false,
          },
        });
      } else {
        dispatch('SET_TRANSCRIPTION', {
          assetUUID,
          value: {
            status: 'UPDATED',
            isOriginal,
            tokens,
            shouldPostNew: false,
          },
        });
      }

      return new Promise((resolve, reject) => {
        const apiMethod = shouldPostNew
          ? app.api.Transcriptions.POST_EMPTY
          : app.api.Transcriptions.PUT;
        apiMethod({
          assetUUID,
          tokens: isOriginal ? [] : tokens,
          storyId: state.storyId,
        })
          .then(() => {
            resolve();
          })
          .catch((e) => {
            dispatch('SET_TRANSCRIPTION', {
              assetUUID,
              value: originalValues,
            });
            app.doctorController.report(
              `transcription::save>fail ${JSON.stringify(e)}`
            );
            app.notificationController.push({
              uniqueGroupName: 'TRANSCRIPTIONS_FAIL',
              text: app.culture.get('transcribe.saveFail'),
              type: 'ERROR',
              timeout: 2500,
            });
            reject(e);
          });
      });
    },

    SET_TRANSCRIPTION({ state, commit, getters }, payload) {
      commit('setTranscription', payload);

      const { assetUUID } = payload;
      const usedAsAudioTrack = _.some(
        state.audioTracks,
        (at) => assetUUID === at.assetUUID
      );
      const usedAsVisualTrack = _.some(
        getters['timemachine/visualData'],
        (vt) => assetUUID === vt.metadata.assetUUID
      );
      const segmentInTimeline = usedAsAudioTrack || usedAsVisualTrack;

      if (segmentInTimeline) {
        state.transcriptLastModified = Date.now();
        app.storyController.story.invalidateFullPreview();
      }
    },

    POST_INSERT_TRANSCRIPT_CHECK({ state, dispatch }, { asset }) {
      const assetUUID = asset.metadata.assetUUID;
      const transcript = state.transcriptions[assetUUID];
      const shouldTranscribe =
        state.enableVOSubtitles &&
        transcript &&
        /FAILED/.test(transcript.status);

      if (shouldTranscribe) {
        dispatch('FETCH_TRANSCRIPTION', {
          asset,
          transcribeSubType: app.transcribeSubType.VIDEO,
          how: workPool.job.transcriptions.HOW.CREATE,
        });
      }
    },

    STORY_FREE({ state }) {
      workPool.abortAll(workPool.job.transcriptions.type);
      workPool.abortAll(workPool.job.createExternalAsset.type);
      workPool.abortAll(workPool.job.trimVideoFragment.type);
      workPool.off(state.workPoolHandlers);
      for (const fn of state.pluginOffHandlers) {
        fn();
      }

      app.rootStore.unregisterModule('timemachine');
      app.rootStore.unregisterModule('wallpapers');
      // do not use store after that point (no getters)
      app.rootStore.unregisterModule('story');

      timemachineTaskManager.terminate();
    },

    async STORY_INIT(
      { commit, dispatch, state, getters, rootState, rootGetters },
      payload
    ) {
      const { story, store, plugins, storyModel } = payload;
      _.each(plugins, (plugin) => {
        plugin(store);
      });

      timemachineTaskManager.initialize();

      app.rootStore.registerModule('wallpapers', WallpapersStoreModule, {
        preserveState: false,
      });
      app.rootStore.registerModule(
        'timemachine',
        new TimemachineStoreModule({
          CLICK_OUTSIDE_IGNORABLE_SELECTORS:
            '.wt-story-rcp, .wt-timeline-zoom, .wt2-asset-modal',
          SCENE_EDIT_TIMEOUT: {
            ENTER:
              app.stash.get('HOVER_TO_ENTER_SCENE_EDIT_TIMEOUT') ||
              app.TIME.second(2.25),
            LEAVE: app.TIME.second(1.5),
          },
        }),
        { preserveState: false }
      );

      commit('storyInit', payload);

      let { brandColors, brandFonts } = state.themeInfo;
      for (const font of brandFonts) {
        font.value = font.value.replace('/', '__');
      }
      let colorSetFromStoryboard;
      let fontsFromStoryboard;
      const themeVariantOverrides = _.cloneDeep(
        story.timelineData.themeOverrides?.themeVariantOverrides || []
      );
      if (themeVariantOverrides?.length) {
        ({ color: colorSetFromStoryboard, font: fontsFromStoryboard } =
          _.groupBy(themeVariantOverrides, (el) => el.type));
        if (fontsFromStoryboard?.length) {
          for (const font of fontsFromStoryboard) {
            if (font.value) {
              font.value = font.value.replace('/', '__');
            }
          }
        }
      }
      commit('setBrandColors', _.uniqBy(brandColors, 'value'));

      brandColors = brandColors.filter((color) => {
        return color.name && /^color[1-4]/.test(color.name);
      });

      const colorSets = videoStyle.colorSets;
      normalizeColorSet(brandColors);
      normalizeColorSet(colorSetFromStoryboard);

      for (const colorSet of colorSets) {
        for (const key of Object.keys(colorSet)) {
          colorSet[key] = normalizeColor(colorSet[key]);
        }
      }
      if (colorSetFromStoryboard?.length) {
        logColorSet(colorSetFromStoryboard, 'Color From Story');
        addColorIfNotExist(colorSets, colorSetFromStoryboard);
      }
      if (brandColors?.length) {
        logColorSet(brandColors, 'Brand Colors');
        addColorIfNotExist(colorSets, brandColors);
      }
      let selectedColorSet;
      if (colorSetFromStoryboard?.length) {
        selectedColorSet = colorSetFromStoryboard;
      } else if (brandColors?.length) {
        selectedColorSet = brandColors;
      } else {
        const colorSet = colorSets[0];
        const colorSetArray = [];
        for (const name in colorSet) {
          if (Object.getOwnPropertyDescriptor(colorSet, name)) {
            colorSetArray.push({ name, value: colorSet[name] });
          }
        }
        selectedColorSet = colorSetArray;
      }

      commit('setRecentColorSets');
      dispatch('SET_COLOR_SETS', colorSets);

      let selectedFonts;

      if (state.fonts?.length) {
        if (fontsFromStoryboard?.length) {
          selectedFonts = fontsFromStoryboard;
        } else if (brandFonts?.length) {
          selectedFonts = brandFonts;
        }
      }

      app.timelineGateway.tuneToStory(storyModel);
      _.eachIn(storyModel.getTimelineData(), 'visualTracks', (asset) => {
        app.setDefaultAssetVolumes({ asset, story: storyModel });
      });

      await dispatch('SET_SELECTED_ASSETS', story.selectedAssets);
      await dispatch('INIT_TIMELINE_DATA', {
        storyModel: storyModel,
      });

      dispatch('SET_STORY_BACKGROUND_MUSIC', story.chosenBGMusic);

      dispatch('SET_ORIGINAL_COLOR_SET', _.cloneDeep(selectedColorSet));
      dispatch('SET_SELECTED_COLOR_SET', {
        colorSet: _.cloneDeep(selectedColorSet),
      });

      if (selectedFonts) {
        dispatch('SET_SELECTED_FONTS', {
          refreshPreview: payload.refreshPreview,
          selectedFonts: _.cloneDeep(selectedFonts),
          brandFonts: _.cloneDeep(brandFonts),
        });
      }

      if (payload.refreshPreview) {
        app.timelineGateway.refreshRtpLookup({ resetUndoRedo: true });
      } else {
        app.timelineGateway.initRtpLookup();
      }

      _.each(state.selectedVoiceOverSegments, (segment) => {
        store.dispatch('FETCH_TRANSCRIPTION', {
          asset: segment.audioSegmentContent.asset,
          transcribeSubType: app.transcribeSubType.NARRATION,
          how: workPool.job.transcriptions.HOW.READ_OR_CREATE,
        });

        workPool.job.audiowaveform.execute({
          asset: segment.audioSegmentContent.asset,
          checkIfExists: true,
        });
      });

      _.each(getters.visualCandidatesForTranscribe, (asset) => {
        store.dispatch('FETCH_TRANSCRIPTION', {
          asset,
          transcribeSubType: app.transcribeSubType.VIDEO,
          how: workPool.job.transcriptions.HOW.READ,
        });
      });

      workPool.on(
        {
          type: workPool.job.transcriptions.type,
          context: (jc) => jc.storyId === state.storyId,
        },
        (res) => {
          const result = workPool.job.transcriptions.parseTranscribeResult({
            assetUUID: res.job.options.assetUUID,
            res,
          });

          dispatch('SET_TRANSCRIPTION', result);

          workPool.job.transcriptions.setDefaultCC({
            assetUUID: res.job.options.assetUUID,
            visualData: app.timelineGateway.getVisualData(),
            hisTranscript: state.transcriptions[res.job.options.assetUUID],
            enableVOSubtitles: state.enableVOSubtitles,
          });

          const showFailureNotification =
            res.failedToCreate && res.job.options.alertOnError;
          if (showFailureNotification) {
            app.notificationController.push({
              text: 'Oops\u2026 some of your assets could not be transcribed.',
              type: 'ERROR',
            });
          }
        },
        state.workPoolHandlers
      );

      const showBeats = !!app.remoteStash.get('showBeats');
      dispatch('timemachine/SET_USE', {
        name: 'showBeats',
        value: showBeats,
      });

      const showTimelineScenes = !!app.remoteStash.get('showTimelineScenes');
      dispatch('timemachine/SET_USE', {
        name: 'showTimelineScenes',
        value: showTimelineScenes,
      });
    },

    INIT_TIMELINE_DATA(
      { state, commit, dispatch, rootState, rootGetters },
      { storyModel }
    ) {
      dispatch(
        'timemachine/INIT_TIMEMACHINE',
        {
          rowDuration: state.timelineZoom.rowDuration,
          timelineData: storyModel.getTimelineData(),
          lightboxAudio: state.selectedVoiceOverSegments,
          audioTracks: state.audioTracks,
        },
        { root: true }
      ).then(() => {
        dispatch('RECALCULATE_FINAL_STORY_DURATION');

        if (rootGetters['user/isImpededTemplatizer']) {
          dispatch('timemachine/SET_USE', { name: 'sceneEdit', value: false });
        }

        Promise.all([
          app.timelineGateway.whenWidgetReady,
          storyModel.remoteStash.whenReady,
        ]).then(() => {
          const rowDuration = storyModel.remoteStash.get('rowDuration');
          if (
            state.timelineZoom.steps.indexOf(rowDuration) >= 0 &&
            state.timelineZoom.rowDuration !== rowDuration
          ) {
            commit('setTimelineZoom', { rowDuration });
            dispatch('timemachine/CHANGE_ROW_DURATION', rowDuration);
          }
        });
      });
    },

    RECALCULATE_FINAL_STORY_DURATION({ state, commit, rootGetters }) {
      commit(
        'setTimelineDuration',
        rootGetters['timemachine/maxLayerDuration']
      );

      let fsd = state.timelineDuration;
      let themeInfo = state.themeInfo;
      const storyModel = app.storyController.story;

      if (fsd > 0 && !_.isEmpty(themeInfo)) {
        // using compactObject cause sometimes some values are null
        themeInfo = _.extend(
          {
            closerDuration: 0,
            hasWiper: false,
            openerDuration: 0,
            wiperDuration: 0,
          },
          _.compactObject(themeInfo)
        );

        fsd += themeInfo.closerDuration;
        fsd += themeInfo.openerDuration;
        if (themeInfo.hasWiper) {
          fsd += storyModel.getWipersCount() * themeInfo.wiperDuration;
        }
      }

      commit('setFinalStoryDuration', fsd);
    },

    SET_SELECTED_ASSETS({ commit }, assets) {
      const parsedList = [];

      _.each(assets, (asset) => {
        if (_.isEmpty(asset.metadata.assetUUID)) {
          console.warn('AssetCollection::parse-> missing assetUUID');
          return true; // continue
        }

        if (
          app.assetType.isIMAGE(asset) ||
          app.assetType.isWALLPAPER(asset) ||
          app.assetType.isGIF(asset) ||
          app.assetType.isVIDEO(asset) ||
          app.assetType.isFRAGMENT(asset) ||
          app.assetType.isWEBPAGE(asset) ||
          app.assetType.isSOCIAL(asset)
        ) {
          const parsedAsset =
            app.namespace.Controller.AssetController.parse(asset);
          parsedList.push(_.cloneDeep(parsedAsset));
        } else {
          console.warn(
            'AssetCollection::parse -> asset type not supported',
            asset
          );
        }
      });

      _.each(parsedList, (asset) => {
        // todo
        commit('addSelectedAsset', { asset, isUserSelection: false });
      });
    },

    UPDATE_SELECTED_ASSET({ commit }, payload) {
      commit('updateSelectedAsset', payload);
    },

    ASSET_SELECTED(
      { commit, state },
      { asset, isNarration = false, transcriptLanguageCode }
    ) {
      return new Promise((resolve, reject) => {
        app.namespace.Controller.AssetController.parse(asset);
        const mutationName = isNarration
          ? 'addSelectedVoiceOverSegment'
          : 'addSelectedAsset';
        const collection = isNarration
          ? state.selectedVoiceOverSegments
          : state.selectedAssets;
        const itExist = _.some(collection, (a) => a.id === asset.id);

        if (itExist) {
          reject();
        } else {
          const clone = _.cloneDeep(asset);

          if (app.assetType.isVIDEO(clone)) {
            if (
              clone.metadata.duration >
              2 * app.DEFAULT.videoDurationPadding
            ) {
              app.namespace.Controller.AssetController.makeVideoFragment(clone);
              commit(mutationName, {
                asset: clone,
                isUserSelection: true,
                transcriptLanguageCode,
              });
              resolve();
            } else {
              reject();
            }
          } else {
            commit(mutationName, {
              asset: clone,
              isUserSelection: true,
              transcriptLanguageCode,
            });
            resolve();
          }
        }
      });
    },

    ASSET_UNSELECTED({ commit }, asset) {
      commit('removeSelectedAsset', asset);
    },

    VOICEOVER_SEGMENT_UNSELECTED({ commit }, segment) {
      commit('removeSelectedVoiceOverSegment', segment);
    },

    UNSELECT_ALL_SIMILAR({ commit, state }, asset) {
      const toRemove = _.filter(state.selectedAssets, (a) => {
        return a.metadata.assetUUID === asset.metadata.assetUUID;
      });
      _.each(toRemove, (a) => {
        commit('removeSelectedAsset', a);
      });
    },

    SELECTED_ASSET_METADATA_EDITED({ commit }, payload) {
      commit('selectedAssetMetadataEdited', payload);
    },

    SET_ASSETS_SOURCE({ commit, dispatch }, { source, search = false }) {
      commit('setQuery', { text: '', value: '' });
      commit('resetSelectedTags');
      commit('setAssetsSource', source);
      if (search) {
        dispatch('SEARCH_ASSETS');
      }
    },

    ABORT_LIGHTBOX_UPLOAD_ASSET({ commit }, asset) {
      // FEATURE: abort at uploadQueue if still running
      commit('removeLightboxUploadingAsset', asset);
    },

    ABORT_LIGHTBOX_UPLOAD_VOICE_OVER_ASSET({ commit }, asset) {
      // FEATURE: abort at uploadQueue if still running
      commit('removeLightboxUploadingVoiceOverAsset', asset);
    },

    LIGHTBOX_UPLOAD_DONE({ commit, dispatch }, asset) {
      commit('removeLightboxUploadingAsset', asset);
      dispatch('ASSET_SELECTED', { asset });
    },

    LIGHTBOX_VOICEOVER_UPLOAD_DONE({ commit, dispatch }, asset) {
      commit('removeLightboxUploadingVoiceOverAsset', asset);
      dispatch('ASSET_SELECTED', { asset, isNarration: true });
    },

    SET_PINNED_ASSET({ commit }, { assetId, isPinned }) {
      commit('setPinnedAsset', { assetId, isPinned });
    },

    CHANGE_THEME_INFO({ commit, dispatch }, { themeInfo }) {
      commit('changeThemeInfo', { themeInfo });
      dispatch('RECALCULATE_FINAL_STORY_DURATION');
    },

    SET_STORY_BACKGROUND_MUSIC({ commit, dispatch }, value) {
      app.trigger('story:rcp:pause');
      commit('setStoryBackgroundMusic', value);
      if (value) {
        dispatch('FETCH_BG_MUSIC_BEATS');
      } else {
        commit('clearBgMusicBeats');
      }
    },

    UNDELETE_VOICEOVER_SEGMENTS({ commit }) {
      commit('undeleteVoiceOverSegment');
    },
    RESET_VIDEO_STYLE({ commit, dispatch }) {
      commit('resetVideoStyle');
      app.timelineGateway.refreshRtpLookup({ resetUndoRedo: true });
      app.storyController.story.hasBeenChanged();
    },
    ADD_RECENT_COLOR_SET({ commit }) {
      commit('addRecentColorSet');
    },

    async FETCH_SCENES({ state, commit }, { assetId }) {
      // check if scenes are already fetched
      if (state.scenes[assetId]) {
        return state.scenes[assetId];
      }

      try {
        // First try to get existing scenes
        const scenes = await app.api.sceneDetection.get(assetId);
        const finalScenes = scenes.length > 1 ? scenes : [];

        commit('setScenes', { assetId, scenes: finalScenes });
        return finalScenes;
      } catch (error) {
        // If get fails, try to create scenes
        try {
          await app.api.sceneDetection.post(assetId);
          // Wait for scene detection to complete
          await app.api.realTime.listenAssetScenes(assetId);
          // Get the newly created scenes
          const scenes = await app.api.sceneDetection.get(assetId);
          const finalScenes = scenes.length > 1 ? scenes : [];
          commit('setScenes', { assetId, scenes: finalScenes });
          return finalScenes;
        } catch (error) {
          // For now, just set empty scenes array on error
          commit('setScenes', { assetId, scenes: [] });
          return [];
        }
      }
    },
  },
};
